import React, { useEffect, useState, useMemo } from 'react';
import './MaintenanceLogList.css';
import { LogType } from '../interfaces/LogType';
import MaintenanceDetailModal from './MaintenanceDetailModal';
import { getLogCount, loadLogs } from '../firebase/db-logs';
import { FilterType } from '../interfaces/FilterType';
import LoadingSpinner from './LoadingSpinner';

interface MaintenanceListProps {
  itemsPerPage?: number;
  droneType?: string | number;
  filters?: FilterType[];
}

const MaintenanceList: React.FC<MaintenanceListProps> = ({
  itemsPerPage = 10,
  droneType = 'Matrice350RTK',
  filters,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [logs, setLogs] = useState<LogType[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [logCount, setLogCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortColumn, setSortColumn] = useState<string>('flightNumber');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const [selectedLog, setSelectedLog] = useState<LogType | null>(null);
  const [selectedLogNum, setSelectedLogNum] = useState<number>(0);

  const appliedFilters = useMemo<FilterType[]>(() => {
    return (
      filters ?? [{ field: 'droneType', operator: '==', value: droneType }]
    );
  }, [filters, droneType]);

  const logsWithFlightNumbers = useMemo(() => {
    const sortedByDate = [...logs].sort(
      (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );

    return sortedByDate.map((log, index) => ({
      ...log,
      flightNumber: index + 1,
    }));
  }, [logs]);

  useEffect(() => {
    loadData();
  }, [appliedFilters]);

  async function loadData() {
    console.log('load');
    const tempCount = await getLogCount(appliedFilters);
    setTotalPages(Math.ceil(tempCount / itemsPerPage));
    setLogCount(tempCount);
    await loadInitPage();
    console.log('load finished');
  }

  async function loadInitPage() {
    setIsLoading(true);
    const tempLogs = await loadLogs(itemsPerPage, undefined, appliedFilters);
    if (tempLogs === undefined) {
      setIsLoading(false);
      return;
    }
    setLogs(tempLogs);
    setIsLoading(false);
  }

  async function loadNextPage() {
    setIsLoading(true);
    const tempLogs = await loadLogs(
      itemsPerPage,
      logs[logs.length - 1],
      appliedFilters
    );
    if (tempLogs === undefined) {
      setIsLoading(false);
      return;
    }
    setLogs((currentLogs) => [...currentLogs, ...tempLogs]);
    setIsLoading(false);
  }

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const calculateDuration = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationSeconds = Math.floor(
      (end.getTime() - start.getTime()) / 1000
    );
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };

  const formatDate = (date: Date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const checkForErrors = (log: LogType): boolean => {
    const { errors } = log;
    return (
      errors.waveError ||
      errors.isMotorBlocked ||
      errors.isAcceletorOverRange ||
      errors.isBarometerDeadInAir ||
      errors.isCompassError ||
      errors.isNotEnoughForce ||
      errors.isPropellerCatapult ||
      errors.isVibrating ||
      errors.isStuck
    );
  };

  const sortedLogs = useMemo(() => {
    const sorted = [...logsWithFlightNumbers].sort((a, b) => {
      let compareResult = 0;
      switch (sortColumn) {
        case 'flightNumber':
          compareResult = a.flightNumber - b.flightNumber;
          break;
        case 'date':
          compareResult =
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
          break;
        case 'flightDuration':
          const durationA =
            new Date(a.endTime).getTime() - new Date(a.startTime).getTime();
          const durationB =
            new Date(b.endTime).getTime() - new Date(b.startTime).getTime();
          compareResult = durationA - durationB;
          break;
        case 'hasError':
          const aHasError = checkForErrors(a);
          const bHasError = checkForErrors(b);
          compareResult = aHasError === bHasError ? 0 : aHasError ? -1 : 1;
          break;
        default:
          break;
      }
      return sortDirection === 'asc' ? compareResult : -compareResult;
    });
    return sorted;
  }, [logsWithFlightNumbers, sortColumn, sortDirection]);

  const getPaginatedLogs = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedLogs.slice(startIndex, endIndex);
  };

  const paginatedLogs = getPaginatedLogs();

  const goToNextPage = async () => {
    if (currentPage < totalPages) {
      if (logs.length < itemsPerPage * currentPage + 1) {
        await loadNextPage();
      }
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const calculateMaxSpeedKmH = (log: LogType) => {
    const maxSpeedKmH = log.xSpeedMax * 3.6;
    return Math.round(maxSpeedKmH);
  };

  const handleRowClick = (log: LogType, logNumber: number) => {
    setSelectedLog(log);
    setSelectedLogNum(logNumber);
  };

  const handleCloseModal = () => {
    setSelectedLog(null);
  };

  return (
    <>
      <div className="maintenance-log-table-container">
        <table className="maintenance-log-table">
          <thead>
            <tr>
              <th
                className="maintenance-clickable-header"
                onClick={() => handleSort('flightNumber')}
              >
                <div>Flug</div>
              </th>
              <th
                className="maintenance-clickable-header"
                onClick={() => handleSort('date')}
              >
                <div>Datum</div>
              </th>
              <th>
                <div>Ort</div>
              </th>
              <th
                className="maintenance-clickable-header"
                onClick={() => handleSort('flightDuration')}
              >
                <div>Flugzeit</div>
              </th>
              <th>
                <div>Verbrauch</div>
              </th>
              <th>
                <div>max. Höhe</div>
              </th>
              <th>
                <div>max. km/h</div>
              </th>
              <th>
                <div>min. Temp</div>
              </th>
              <th>
                <div>max. Temp</div>
              </th>
              <th
                className="maintenance-clickable-header"
                onClick={() => handleSort('hasError')}
              >
                <div>Fehler</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && sortedLogs.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: 'center', padding: '20px' }}
                >
                  <img
                    src="/img/no-logs.svg"
                    alt="No logs available"
                    style={{ width: '150px', marginBottom: '20px' }}
                  />
                  <p>Keine Fluglogs gefunden</p>
                </td>
              </tr>
            )}
            {paginatedLogs.map((log, index) => {
              const startDate = new Date(log.startTime);
              const flightDuration = calculateDuration(
                log.startTime,
                log.endTime
              );
              const hasError = checkForErrors(log);
              const flightTimeInMinutes =
                (new Date(log.endTime).getTime() -
                  new Date(log.startTime).getTime()) /
                60000;
              const batteryConsumption =
                log.battery.chargeLevelStart - log.battery.chargeLevelEnd;

              const consumptionPerMinute =
                flightTimeInMinutes > 0
                  ? batteryConsumption / flightTimeInMinutes
                  : 0;
              const roundedConsumptionPerMinute = Math.round(consumptionPerMinute * 10) / 10;

              const maxSpeedKmH = calculateMaxSpeedKmH(log);
              const roundedHeightMax = Math.round(log.heightMax);
              const roundedMinTemp = Math.round(log.battery.minTemperature);
              const roundedMaxTemp = Math.round(log.battery.maxTemperature);
              const logNumber =
                logCount - index - (currentPage - 1) * itemsPerPage;

              return (
                <tr
                  key={log.logId}
                  onClick={() => handleRowClick(log, logNumber)}
                  className="clickable-row"
                >
                  <td>
                    <div className="cell-content">{logNumber}</div>
                  </td>
                  <td>
                    <div className="cell-content">{formatDate(startDate)}</div>
                  </td>
                  <td>
                    <div className="cell-content">
                      {log.location ? log.location : 'Unbekannt'}
                    </div>
                  </td>
                  <td>
                    <div className="cell-content">{flightDuration}</div>
                  </td>
                  <td>
                    <div className="cell-content">{roundedConsumptionPerMinute}% <br/> pro min</div>
                  </td>
                  <td>
                    <div className="cell-content">{roundedHeightMax} m</div>
                  </td>
                  <td>
                    <div className="cell-content">{maxSpeedKmH} km/h</div>
                  </td>
                  <td>
                    <div className="cell-content">{roundedMinTemp} °C</div>
                  </td>
                  <td>
                    <div className="cell-content">{roundedMaxTemp} °C</div>
                  </td>
                  <td>
                    <div className="cell-content">
                      {hasError ? 'Ja' : 'Nein'}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          className="pagination-button"
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
        >
          <span className="material-icons-pagination">arrow_back</span>
        </button>
        <span className="pagination-info">
          Seite {currentPage} von {totalPages}
        </span>
        <button
          className="pagination-button"
          onClick={goToNextPage}
          disabled={currentPage === totalPages || isLoading}
        >
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <span className="material-icons-pagination">arrow_forward</span>
            </>
          )}
        </button>
      </div>

      {selectedLog && (
        <MaintenanceDetailModal
          log={selectedLog}
          logNumber={selectedLogNum}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default MaintenanceList;
